import React, { useMemo } from "react";
import dayjs from "dayjs";

import { PrimaryButton, SecondaryButton, Text } from "widgets";
import Loader from "Shared/Loader/Loader";
import { useNavigate } from "react-router-dom";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { DISCOUNT_TYPE, ZERO } from "constants/config";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { buildAddressPayload } from "utils/addressPayload";
import { buildCustomerOrdersUrl } from "utils/customerFlowUrls";
import { parseDateWithFormat } from "utils/parseDateWithFormat";
import {
  useCountryIsdList,
  findCountryInIsdList,
} from "services/mdsService";
import {
  useTenantsByVerticalId,
  filterTenantsByCountry,
} from "services/tenantService";
import {
  useFetchGatewayTenants,
  useRequestTransferOrder,
} from "store/backend/assets";

import styles from "./RequestTransferOrder.module.scss";

const buildSkuPayload = (selectedPackageSubscriptions) =>
  selectedPackageSubscriptions.reduce((acc, selectedPackage) => {
    const existingPackage = acc.find(
      (result) => result.id === selectedPackage.packageId
    );

    if (existingPackage) {
      return acc.map((result) =>
        result.id === selectedPackage.packageId
          ? {
              ...result,
              subscriptions: [
                ...result.subscriptions,
                { id: selectedPackage.id },
              ],
            }
          : result
      );
    }

    return [
      ...acc,
      {
        id: selectedPackage.packageId,
        subscriptions: [{ id: selectedPackage.id }],
      },
    ];
  }, []);

const buildDiscountPayload = (
  discountDetails,
  listedOrderAmount,
  orderAmount
) => {
  if (!discountDetails?.discount_type) {
    return null;
  }

  const computedDiscount = Number(listedOrderAmount) - Number(orderAmount);
  const discount = reduceToFixedDecimals(
    Number.isNaN(computedDiscount) ? ZERO : Math.max(ZERO, computedDiscount)
  );

  if (discountDetails.discount_type === DISCOUNT_TYPE.DYNAMIC) {
    return {
      discount_type: discountDetails.discount_type,
      discount,
    };
  }

  if (discountDetails.discount_type === DISCOUNT_TYPE.COUPON_CODE) {
    if (!discountDetails.coupon_code) {
      return null;
    }
    return {
      discount_type: discountDetails.discount_type,
      coupon_code: discountDetails.coupon_code,
      discount,
    };
  }

  return null;
};

const totalMrp = (selectedPackageSubscriptions) =>
  selectedPackageSubscriptions.reduce((total, packageSubscription) => {
    return total + (packageSubscription.mrp || 0);
  }, 0);

const formatServiceStartDateForV2 = (serviceStartDate) => {
  if (!serviceStartDate) {
    return serviceStartDate;
  }

  const parsed = parseDateWithFormat(serviceStartDate, "DD-MM-YYYY");
  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  const isoParsed = dayjs(serviceStartDate);
  return isoParsed.isValid() ? isoParsed.format("YYYY-MM-DD") : serviceStartDate;
};

const buildPayload = (
  values,
  newUamId,
  {
    lockedCountryId,
    lockedIsdCode,
    lockedCountryCode,
  },
  gatewayTenantId
) => {
  const {
    orderDetails,
    selectedPackageSubscriptions,
    priceDetailsAfterDiscount,
  } = values;

  const listedOrderAmount =
    priceDetailsAfterDiscount.listedOrderAmount ||
    totalMrp(selectedPackageSubscriptions);

  const orderAmount =
    priceDetailsAfterDiscount.orderAmount ??
    totalMrp(selectedPackageSubscriptions);

  const discountPayload = buildDiscountPayload(
    orderDetails.discount,
    listedOrderAmount,
    orderAmount
  );

  const discount =
    !!discountPayload && !!discountPayload.discount_type
      ? discountPayload
      : null;

  const addressCountry = {
    countryId: lockedCountryId,
    isdCode: lockedIsdCode,
    countryCode: lockedCountryCode,
  };

  const billing_address = buildAddressPayload(
    orderDetails.billingAddress,
    addressCountry
  );

  const requiresShipping = Boolean(orderDetails.hasHardware);
  const shipping_address = requiresShipping
    ? values.useSameAddress
      ? billing_address
      : buildAddressPayload(orderDetails.shippingAddress, addressCountry)
    : null;

  return {
    gateway_tenant_id: gatewayTenantId,
    amount: reduceToFixedDecimals(orderAmount),
    attributed_to:
      orderDetails.attributedTo != null
        ? String(orderDetails.attributedTo)
        : null,
    customer_id: String(newUamId),
    service_start_date: formatServiceStartDateForV2(
      orderDetails.serviceStartDate
    ),
    skus: buildSkuPayload(selectedPackageSubscriptions),
    address: {
      billing_address,
      shipping_address,
    },
    discount,
  };
};

const resolveLockedOrderValues = ({
  transferOrderDetails,
  customerCountryId,
  customerIsdCode,
  customerCountryCode,
  countryIsdList,
}) => {
  const orderBillingCountryId =
    transferOrderDetails?.addressDetails?.billingAddress?.countryId ??
    transferOrderDetails?.billingAddress?.countryId ??
    transferOrderDetails?.addressDetails?.billingAddress?.country_id ??
    transferOrderDetails?.billingAddress?.country_id;

  const lockedCountryId =
    orderBillingCountryId != null && orderBillingCountryId !== ""
      ? Number(orderBillingCountryId)
      : customerCountryId != null
        ? Number(customerCountryId)
        : null;

  const lockedCountry = findCountryInIsdList(countryIsdList, lockedCountryId);
  const lockedIsdCode = lockedCountry?.isd_code || customerIsdCode || "";
  const lockedCountryCode =
    lockedCountry?.country_code || customerCountryCode || "";

  return {
    lockedCountryId,
    lockedIsdCode,
    lockedCountryCode,
  };
};

const resolveGatewayTenantId = ({
  selectedGatewayTenantId,
  selectedTenantId,
  omsTenants,
}) => {
  if (selectedGatewayTenantId != null) {
    return selectedGatewayTenantId;
  }

  if (selectedTenantId == null) {
    return null;
  }

  const matchedTenant = omsTenants.find(
    (tenant) => String(tenant.id) === String(selectedTenantId)
  );

  return matchedTenant?.gatewayTenantId ?? null;
};

const handleRequestTransferOrder = (
  values,
  navigate,
  requestTransferOrder,
  uam_id,
  orderId,
  selectedBusinessVerticalId,
  openSnackbar,
  lockedOrderValues,
  gatewayTenantId
) => {
  const payload = buildPayload(
    values,
    uam_id,
    lockedOrderValues,
    gatewayTenantId
  );
  const requestTransferOrderPayload = { ...payload, order_id: orderId };

  requestTransferOrder(requestTransferOrderPayload, {
    onSuccess: () => {
      openSnackbar("Order transfer requested successfully.", "success");
      navigate(
        buildCustomerOrdersUrl({
          uamId: uam_id,
          verticalId: selectedBusinessVerticalId,
          countryId: lockedOrderValues.lockedCountryId,
        })
      );
    },
    onError: (error) => {
      openSnackbar(` ${error.message || "Please try again later."}`, "error");
    },
  });
};

const RequestTransferOrder = (props) => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const {
    values,
    uam_id,
    orderId,
    selectedBusinessVerticalId,
    transferOrderDetails,
    customerCountryId,
    customerIsdCode,
    customerCountryCode,
    onCancel,
  } = props;

  const {
    mutate: requestTransferOrder,
    isPending: requestTransferOrderLoading,
  } = useRequestTransferOrder();

  const { data: countryIsdList = [] } = useCountryIsdList();

  const lockedOrderValues = resolveLockedOrderValues({
    transferOrderDetails,
    customerCountryId,
    customerIsdCode,
    customerCountryCode,
    countryIsdList,
  });

  const selectedVerticalId = values?.filters?.selectedVerticalId;
  const selectedTenantId = values?.filters?.selectedTenantId;
  const selectedGatewayTenantId = values?.filters?.selectedGatewayTenantId;

  const countryIdForCenters =
    lockedOrderValues.lockedCountryId ?? customerCountryId;

  const { data: verticalScopedTenants = [] } = useTenantsByVerticalId(
    selectedVerticalId
  );

  const countryScopedTenants = useMemo(
    () => filterTenantsByCountry(verticalScopedTenants, countryIdForCenters),
    [verticalScopedTenants, countryIdForCenters]
  );

  const countryScopedGatewayTenantIds = useMemo(
    () => countryScopedTenants.map((tenant) => tenant.id),
    [countryScopedTenants]
  );

  const { data: countryScopedOmsTenants = [] } = useFetchGatewayTenants(
    selectedVerticalId,
    countryScopedGatewayTenantIds
  );

  const gatewayTenantId = resolveGatewayTenantId({
    selectedGatewayTenantId,
    selectedTenantId,
    omsTenants: countryScopedOmsTenants,
  });

  return (
    <div className={styles.popOverContainer}>
      <Text>Are you sure you want to transfer the order ?</Text>
      <div className={styles.flexContainer}>
        <SecondaryButton onClick={onCancel}>Go Back</SecondaryButton>
        <PrimaryButton
          onClick={() => {
            handleRequestTransferOrder(
              values,
              navigate,
              requestTransferOrder,
              uam_id,
              orderId,
              selectedBusinessVerticalId,
              openSnackbar,
              lockedOrderValues,
              gatewayTenantId
            );
          }}
          disabled={
            lockedOrderValues.lockedCountryId == null ||
            !lockedOrderValues.lockedCountryCode ||
            !lockedOrderValues.lockedIsdCode ||
            selectedTenantId == null ||
            gatewayTenantId == null
          }
        >
          {requestTransferOrderLoading ? <Loader /> : "Confirm and Proceed"}
        </PrimaryButton>
      </div>
    </div>
  );
};
export default RequestTransferOrder;
